import type { BreakdownDocumentContents } from '@jobos/shared';

export interface DocSubItemInput {
  id: string;
  type: string;
  hours?: number | null;
  rate?: number | null;
  rateType?: 'hourly' | 'daily' | null;
  unitKey?: string | null;
  amount?: number | null;
  note?: string | null;
  markupRate?: number | null;
}

export interface DocLineItemInput {
  id: string;
  category: string;
  description?: string | null;
  sortOrder?: number;
  templateId?: string | null;
  itemDate?: string | null;
  projectId?: string | null;
  subItems: DocSubItemInput[];
}

export interface DocProjectInput {
  id: string;
  name: string;
  sortOrder?: number;
}

export interface BuildDocContentsInput {
  taxRate: number;
  markupRate: number;
  roundingPrecision: number | null;
  projects?: DocProjectInput[];
  lineItems: DocLineItemInput[];
}

// Single source of truth for the BreakdownDocumentContents payload shape.
// Normalizes optional fields to null and assigns positional sortOrder so the
// generic editor and the specialty (carpet/equipment) save hooks all serialize
// identically. The server recomputes totalCost from these values.
export function buildDocContents(input: BuildDocContentsInput): BreakdownDocumentContents {
  return {
    taxRate: input.taxRate,
    markupRate: input.markupRate,
    roundingPrecision: input.roundingPrecision,
    projects: (input.projects ?? []).map((p, i) => ({
      id: p.id,
      name: p.name,
      sortOrder: p.sortOrder ?? i,
    })),
    lineItems: input.lineItems.map((li, liIdx) => ({
      id: li.id,
      category: li.category,
      description: li.description ?? null,
      sortOrder: li.sortOrder ?? liIdx,
      templateId: li.templateId ?? null,
      itemDate: li.itemDate ?? null,
      projectId: li.projectId ?? null,
      subItems: li.subItems.map((s, i) => ({
        id: s.id,
        type: s.type,
        hours: s.hours ?? null,
        rate: s.rate ?? null,
        rateType: s.rateType ?? null,
        unitKey: s.unitKey ?? undefined,
        amount: s.amount ?? null,
        note: s.note ?? null,
        markupRate: s.markupRate ?? null,
        sortOrder: i,
      })),
    })),
  };
}
